import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";

// The one "you have unsaved edits" question, asked in one place.
//
// Lifted verbatim out of UnsavedGuard when a SECOND thing turned out to be able to destroy
// in-progress edits: changing the acting-company context empties the Apollo cache (ADR
// 0034-acting-company-context-resets-the-apollo-cache), which remounts every editor under it. Both
// occasions are the same question with the same three answers, so they are one component rather than
// two that have to be kept saying the same thing.
//
// Presentational only. It holds no dirty state, performs no save and decides nothing about what
// "proceed" means — the caller owns all three (see lib/unsaved-edits-gate.ts and UnsavedGuard).

interface UnsavedChangesDialogProps {
  open: boolean;
  /** What is about to destroy the edits. Defaults to the navigation wording. */
  body?: ReactNode;
  saving: boolean;
  /** Already localized: what the caller's failed save said. Empty when nothing failed. */
  error: string;
  onStay: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

export function UnsavedChangesDialog({
  open,
  body,
  saving,
  error,
  onStay,
  onDiscard,
  onSave,
}: UnsavedChangesDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onStay}
      title={<FormattedMessage id="unsavedGuard.title" defaultMessage="Unsaved changes" />}
    >
      <p className="mb-5 text-[13px] leading-relaxed text-ink/70">
        {body ?? (
          <FormattedMessage
            id="unsavedGuard.body"
            defaultMessage="You have changes that haven't been saved yet. If you leave this page, they will be lost."
          />
        )}
      </p>
      {error ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onDiscard} disabled={saving}>
          <FormattedMessage id="unsavedGuard.discard" defaultMessage="Discard" />
        </Button>
        <Button variant="secondary" onClick={onStay} disabled={saving}>
          <FormattedMessage id="unsavedGuard.stay" defaultMessage="Stay here" />
        </Button>
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving…" />
          ) : (
            <FormattedMessage id="unsavedGuard.save" defaultMessage="Save" />
          )}
        </Button>
      </div>
    </Dialog>
  );
}
